import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Typography,
  Paper,
  Box,
  Chip,
  Divider
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
// ✅ FASE 2: Importar modal de preview de cambios
import ConfigChangePreviewModal from "../ConfigChangePreviewModal";
// ✅ NUEVO: Importar componentes mejorados de selección
import PromptSelect from "../PromptSelect";
import IntegrationSelect from "../IntegrationSelect";
// ✅ NUEVO: Importar modal de advertencia de conexión desconectada
import ConnectionWarningModal from "../ConnectionWarningModal";
import CampaignMessageComposer from "../CampaignMessageComposer";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: theme.palette.success.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  tabContent: {
    marginTop: theme.spacing(1.5),    // ✅ Reducido de 2 a 1.5
  },
  // ✅ OPTIMIZADO: SectionHeader más compacto
  sectionHeader: {
    marginBottom: theme.spacing(1.5), // ✅ Reducido de 2 a 1.5
    padding: theme.spacing(1.5),      // ✅ Reducido de 2 a 1.5
    backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.grey[300]}`,
  },
  // ✅ OPTIMIZADO: CheckboxSection más compacto
  checkboxSection: {
    marginBottom: theme.spacing(2),   // ✅ Reducido de 3 a 2
    padding: theme.spacing(1.5),      // ✅ Reducido de 2 a 1.5
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
  },
  fieldGroup: {
    marginTop: theme.spacing(1.5),    // ✅ Reducido de 2 a 1.5
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    minWidth: 200,
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },
  // ✅ NUEVO: TextField compacto para campos numéricos
  textFieldCompact: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      height: '40px', // ✅ Altura reducida
      "& input": {
        padding: '8px 12px', // ✅ Padding reducido
        fontSize: '0.875rem', // ✅ Fuente más pequeña
      },
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontSize: '0.875rem', // ✅ Label más pequeño
      transform: 'translate(14px, 12px) scale(1)', // ✅ Ajuste para label más pequeño
      "&.MuiInputLabel-shrink": {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontSize: '0.75rem', // ✅ Helper text más pequeño
      marginTop: '4px',
    },
  },
  // ✅ NUEVO: FormControl compacto para Selects
  formControlCompact: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      height: '40px', // ✅ Altura reducida
      "& .MuiSelect-select": {
        padding: '8px 12px', // ✅ Padding reducido
        fontSize: '0.875rem', // ✅ Fuente más pequeña
        minHeight: 'auto', // ✅ Eliminar minHeight
      },
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontSize: '0.875rem', // ✅ Label más pequeño
      transform: 'translate(14px, 12px) scale(1)',
      "&.MuiInputLabel-shrink": {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      padding: '6px 12px', // ✅ Padding reducido en menu items
      fontSize: '0.875rem', // ✅ Fuente más pequeña
      minHeight: '36px', // ✅ Altura reducida
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
      },
    },
  },
  // ✅ CORREGIDO: FormControl dinámico según tema
  formControl: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
      },
    },
  },
  // ✅ CORREGIDO: DialogContent dinámico según tema
  dialogContent: {
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
  },
  // ✅ CORREGIDO: DialogActions dinámico según tema
  dialogActions: {
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
    borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
  },
  // ✅ OPTIMIZADO: TabPanel más compacto
  tabPanel: {
    padding: theme.spacing(2),        // ✅ Reducido de 3 a 2
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
  },
  // ✅ NUEVO: Estilos para hacer componentes Select personalizados más compactos
  compactSelectWrapper: {
    "& .MuiFormControl-root": {
      "& .MuiOutlinedInput-root": {
        height: '40px', // ✅ Altura reducida
        "& .MuiSelect-select": {
          minHeight: 'auto !important', // ✅ Eliminar minHeight de 50px
          padding: '8px 12px !important', // ✅ Padding reducido
          fontSize: '0.875rem !important', // ✅ Fuente más pequeña
          display: 'flex',
          alignItems: 'center',
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: '0.875rem', // ✅ Label más pequeño
        transform: 'translate(14px, 12px) scale(1)',
        "&.MuiInputLabel-shrink": {
          transform: 'translate(14px, -6px) scale(0.75)',
        },
      },
      "& .MuiChip-root": {
        height: '24px', // ✅ Chips más pequeños
        fontSize: '0.75rem', // ✅ Fuente más pequeña
        "& .MuiChip-label": {
          padding: '4px 8px', // ✅ Padding reducido
        },
      },
    },
  },
  // ✅ NUEVO: Bloque visual para FlowBuilder en Connections
  flowbuilderCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.mode === "dark" ? "#4B5563" : theme.palette.primary.light}`,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f5f9ff"
  },
  flowbuilderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  flowbuilderTitle: {
    fontSize: "0.95rem",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  flowbuilderBody: {
    fontSize: "0.8rem"
  },
  flowbuilderChips: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  flowbuilderActions: {
    marginTop: theme.spacing(1.5),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
}));

// Componente para el contenido de las pestañas
function TabPanel(props) {
  const { children, value, index, className, ...other } = props;

  return (
    <div
      role="tabpanel"
      // ✅ SOLUCIÓN DEFINITIVA: Usar maxHeight y overflow en lugar de display/position
      // Los campos SIEMPRE están en el flujo del documento normal para que Formik los capture
      style={{ 
        display: 'block', // ✅ SIEMPRE block
        maxHeight: value !== index ? '0' : 'none', // ✅ Colapsar altura cuando está oculto
        overflow: value !== index ? 'hidden' : 'visible', // ✅ Ocultar overflow
        opacity: value !== index ? 0 : 1, // ✅ Ocultar visualmente
        pointerEvents: value !== index ? 'none' : 'auto', // ✅ Desactivar interacción cuando está oculto
        transition: 'opacity 0.2s ease-in-out' // ✅ Transición suave
      }}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className={className}
      {...other}
    >
      {/* Renderizar siempre los hijos - Formik captura los valores porque están en el flujo normal */}
      {children}
    </div>
  );
}

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});

const initialState = {
  name: "",
  greetingMessage: "",
  complationMessage: "",
  outOfHoursMessage: "",
  ratingMessage: "",
  isDefault: false,
  token: "",
  status: "OPENING",
  provider: "beta",
  expiresInactiveMessage: "",
  expiresTicket: 0,
  timeUseBotQueues: 0,
  maxUseBotQueues: 3,
  timeToTransfer: "",
  type: "whatsapp",
  telegramApiId: "",
  telegramApiHash: ""
};

const WhatsAppModal = ({ open, onClose, whatsAppId, onSave, connectionType = "whatsapp" }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [whatsApp, setWhatsApp] = useState(initialState);
  
  // ✅ SIMPLIFICADO: Solo un departamento por conexión (cada departamento es independiente)
  const [selectedQueueId, setSelectedQueueId] = useState(''); // ✅ Departamento de la conexión (pestaña CONEXIÓN)
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState('');
  // ✅ REMOVIDO: prompts e integrations ya no se usan directamente (los componentes PromptSelect e IntegrationSelect los cargan)
  const [isLoading, setIsLoading] = useState(false);
  
  // ✅ Estados para transferencias por tiempo
  const [transferQueueId, setTransferQueueId] = useState(''); // ✅ Departamento destino para transferencias (pestaña TRANSFERENCIAS)
  const [transferWhatsappId, setTransferWhatsappId] = useState('');
  const [whatsapps, setWhatsapps] = useState([]);
  const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);
  
  // Estados para las pestañas
  const [activeTab, setActiveTab] = useState(0);
  const [tabStates, setTabStates] = useState({
    // ✅ ELIMINADO: chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
    transfersEnabled: false,    // Por defecto DESACTIVADO ✅
    inactivityEnabled: false   // Por defecto DESACTIVADO ✅
  });

  // ✅ FASE 2: Estados para el modal de preview de cambios
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // ✅ NUEVO: Estados para modal de advertencia de conexión desconectada
  const [connectionWarningOpen, setConnectionWarningOpen] = useState(false);
  const [connectionWarningData, setConnectionWarningData] = useState(null);
  const [pendingWhatsAppData, setPendingWhatsAppData] = useState(null);

  // ✅ NUEVO: Estado para FlowBuilder (selector)
  const [selectedFlowBuilder, setSelectedFlowBuilder] = useState('');
  const [flowBuilders, setFlowBuilders] = useState([]);
  const [loadingFlowBuilders, setLoadingFlowBuilders] = useState(false);

  // ✅ MEJORAR CARGA DE DATOS
  useEffect(() => {
    let isMounted = true; // ✅ FLAG PARA VERIFICAR SI EL COMPONENTE ESTÁ MONTADO
    
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        setIsLoading(true);
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);
        
        // ✅ VERIFICAR SI EL COMPONENTE SIGUE MONTADO ANTES DE ACTUALIZAR ESTADO
        if (!isMounted) return;
        
        // ✅ Asegurar que no haya valores null en los campos
        // IMPORTANTE: En JavaScript, el último objeto sobrescribe al anterior
        const sanitizedData = {
          ...initialState, // ✅ PRIMERO: Valores por defecto
          ...data, // ✅ SEGUNDO: Datos de la API (sobrescriben valores por defecto)
          // ✅ TERCERO: Asegurar valores específicos (prioridad más alta - sobrescriben todo)
          name: data.name || '',
          greetingMessage: data.greetingMessage || '',
          complationMessage: data.complationMessage || '',
          outOfHoursMessage: data.outOfHoursMessage || '',
          ratingMessage: data.ratingMessage || '',
          token: data.token !== null && data.token !== undefined ? data.token : '', // ✅ Cargar token desde la respuesta (preservar valores vacíos válidos)
          status: data.status || 'OPENING',
          provider: data.provider || 'beta',
          // ✅ CORRECCIÓN: Manejar valores null/undefined correctamente sin usar || que puede ocultar 0 o ''
          expiresInactiveMessage: data.expiresInactiveMessage !== null && data.expiresInactiveMessage !== undefined ? data.expiresInactiveMessage : '',
          expiresTicket: data.expiresTicket !== null && data.expiresTicket !== undefined ? data.expiresTicket : 0,
          timeUseBotQueues: data.timeUseBotQueues !== null && data.timeUseBotQueues !== undefined ? data.timeUseBotQueues : 0,
          maxUseBotQueues: data.maxUseBotQueues !== null && data.maxUseBotQueues !== undefined ? data.maxUseBotQueues : 3,
          isDefault: data.isDefault !== null && data.isDefault !== undefined ? data.isDefault : false,
          timeToTransfer: data.timeToTransfer !== null && data.timeToTransfer !== undefined ? data.timeToTransfer : ''
        };
        
        // ✅ Datos cargados desde la API
        
        if (isMounted) {
          setWhatsApp(sanitizedData);
          
          // ✅ GUARDAR initialValues ESTABLES - Solo se actualiza cuando se cargan datos desde la API
          // Estos valores NO cambian cuando el usuario escribe, solo se usan para inicializar Formik
          const initialValues = {
            ...sanitizedData,
            name: sanitizedData.name || '',
            greetingMessage: sanitizedData.greetingMessage || '',
            complationMessage: sanitizedData.complationMessage || '',
            outOfHoursMessage: sanitizedData.outOfHoursMessage || '',
            ratingMessage: sanitizedData.ratingMessage || '',
            token: sanitizedData.token !== null && sanitizedData.token !== undefined ? sanitizedData.token : '',
            status: sanitizedData.status || 'OPENING',
            provider: sanitizedData.provider || 'beta',
            // ✅ CORRECCIÓN: Manejar valores null/undefined correctamente sin usar || que puede ocultar 0 o ''
            expiresInactiveMessage: sanitizedData.expiresInactiveMessage !== null && sanitizedData.expiresInactiveMessage !== undefined ? sanitizedData.expiresInactiveMessage : '',
            expiresTicket: sanitizedData.expiresTicket !== null && sanitizedData.expiresTicket !== undefined ? sanitizedData.expiresTicket : 0,
            timeUseBotQueues: sanitizedData.timeUseBotQueues !== null && sanitizedData.timeUseBotQueues !== undefined ? sanitizedData.timeUseBotQueues : 0,
            maxUseBotQueues: sanitizedData.maxUseBotQueues !== null && sanitizedData.maxUseBotQueues !== undefined ? sanitizedData.maxUseBotQueues : 3,
            isDefault: sanitizedData.isDefault !== null && sanitizedData.isDefault !== undefined ? sanitizedData.isDefault : false,
            timeToTransfer: sanitizedData.timeToTransfer !== null && sanitizedData.timeToTransfer !== undefined ? sanitizedData.timeToTransfer : ''
          };
          setStableInitialValues(initialValues);

          // ✅ SIMPLIFICADO: Solo un departamento por conexión
          // ✅ CORRECCIÓN: Cargar el departamento de la conexión (pestaña CONEXIÓN)
          const whatsQueueId = data.queues && data.queues.length > 0 ? data.queues[0].id : '';
          setSelectedQueueId(whatsQueueId);
          
          // ✅ DETERMINAR ESTADO DE LAS PESTAÑAS BASADO EN DATOS EXISTENTES
          // ✅ ELIMINADO: chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
          const newTabStates = {
            transfersEnabled: !!(data.timeToTransfer && data.transferQueueId),
            inactivityEnabled: !!(data.expiresTicket && data.expiresInactiveMessage)
          };
          
          // ✅ CORRECCIÓN: Cargar el departamento destino para transferencias (pestaña TRANSFERENCIAS)
          // ✅ Usar una variable separada para no sobrescribir selectedQueueId
          if (data.transferQueueId !== null && data.transferQueueId !== undefined) {
            setTransferQueueId(data.transferQueueId);
          } else {
            setTransferQueueId('');
          }
          
          // ✅ Cargar transferWhatsappId si existe
          if (data.transferWhatsappId !== null && data.transferWhatsappId !== undefined) {
            setTransferWhatsappId(data.transferWhatsappId);
          } else {
            setTransferWhatsappId('');
          }
          
          // ✅ CARGAR CORRECTAMENTE EL PROMPT ID
          // ✅ CORRECCIÓN v1.3.0: Normalizar promptId para evitar valores null
          if (data.promptId !== null && data.promptId !== undefined) {
            setSelectedPrompt(data.promptId);
          }
          
          // ✅ CARGAR CORRECTAMENTE EL FLOWBUILDER ID
          if (data.flowBuilderId !== null && data.flowBuilderId !== undefined) {
            setSelectedFlowBuilder(data.flowBuilderId);
          } else {
            setSelectedFlowBuilder('');
          }
          
          // ✅ CARGAR CORRECTAMENTE EL INTEGRATION ID
          // ✅ CORRECCIÓN v1.3.0: Normalizar integrationId para evitar valores null
          if (data.integrationId !== null && data.integrationId !== undefined && data.integrationId !== "") {
            setSelectedIntegration(Number(data.integrationId));
          } else {
            setSelectedIntegration('');
          }
          
          setTabStates(newTabStates);
        }

      } catch (err) {
        if (isMounted) {
          console.error("❌ ERROR AL CARGAR DATOS:", err);
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchSession();
    
    // ✅ CLEANUP: Marcar como desmontado cuando el componente se desmonte
    return () => {
      isMounted = false;
    };
  }, [whatsAppId]);

  // ✅ REMOVIDO: Los useEffect para cargar prompts e integrations ya no son necesarios
  // Los componentes PromptSelect e IntegrationSelect cargan sus propios datos

  // ✅ Cargar conexiones WhatsApp para transferencias por tiempo
  const { user } = useContext(AuthContext);
  const { companyId } = user || {};
  
  useEffect(() => {
    const loadWhatsapps = async () => {
      if (!companyId) return;
      
      setLoadingWhatsapps(true);
      try {
        const { data } = await api.get(`/whatsapp`, { params: { companyId, session: 0 } });
        setWhatsapps(data || []);
      } catch (err) {
        console.error("Error cargando conexiones WhatsApp:", err);
        toastError(err);
      } finally {
        setLoadingWhatsapps(false);
      }
    };

    loadWhatsapps();
  }, [companyId]);

  // ✅ MANEJAR CAMBIO DE PESTAÑA
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // ✅ MANEJAR CAMBIO DE ESTADO DE CHECKBOXES
  const handleCheckboxChange = (field, checked, setFieldValue) => {
    setTabStates(prev => ({
      ...prev,
      [field]: checked
    }));

    // ✅ LIMPIAR CAMPOS Y ESTADOS RELACIONADOS AL DESACTIVAR
    if (field === 'transfersEnabled' && !checked) {
      // ✅ CORRECCIÓN: Limpiar campo de tiempo, departamento destino Y conexión WhatsApp cuando se desactiva
      setFieldValue('timeToTransfer', '');
      setTransferQueueId(''); // ✅ CORRECCIÓN: Limpiar transferQueueId, NO selectedQueueId (que es el departamento de la conexión)
      setTransferWhatsappId(''); // ✅ CORRECCIÓN: Limpiar también transferWhatsappId
    }
    
    if (field === 'inactivityEnabled' && !checked) {
      // ✅ CORRECCIÓN: Limpiar campos de inactividad cuando se desactiva (usar 0 para expiresTicket, '' para message)
      setFieldValue('expiresTicket', 0);
      setFieldValue('expiresInactiveMessage', '');
    }
    
    // ✅ ELIMINADO: Lógica de chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
  };

  const handleClose = useCallback(() => {
    onClose();
    setWhatsApp(initialState);
    setStableInitialValues(null); // ✅ LIMPIAR initialValues ESTABLES
    setSelectedQueueId(''); // ✅ LIMPIAR departamento de la conexión
    setTransferQueueId(''); // ✅ LIMPIAR departamento destino de transferencias
    setTransferWhatsappId(''); // ✅ CORRECCIÓN: Limpiar también transferWhatsappId
    setSelectedPrompt('');
    setSelectedIntegration('');
    setSelectedFlowBuilder('');
    setIsLoading(false);
    setActiveTab(0);
    setTabStates({
      // ✅ ELIMINADO: chatbotEnabled - Ya no se usa (pestaña CHATBOT eliminada)
      transfersEnabled: false,
      inactivityEnabled: false
    });
  }, [onClose]);

  // ✅ NUEVO: Cargar lista de flujos FlowBuilder cuando se abre el modal
  useEffect(() => {
    if (!open) return;
    
    const loadFlowBuilders = async () => {
      try {
        setLoadingFlowBuilders(true);
        const { data } = await api.get("/flowbuilders", {
          params: { active: true }
        });
        
        setFlowBuilders(data?.records || []);
      } catch (err) {
        console.error("Error cargando flujos FlowBuilder:", err);
        setFlowBuilders([]);
      } finally {
        setLoadingFlowBuilders(false);
      }
    };
    
    loadFlowBuilders();
  }, [open]);

  const handleSaveWhatsApp = useCallback(async (values) => {
    try {
      setIsLoading(true);
      
      // ✅ VALIDACIÓN: Verificar estado de conexión WhatsApp antes de guardar (solo si hay transferWhatsappId)
      if (transferWhatsappId && transferWhatsappId !== "" && transferWhatsappId !== 0) {
        const selectedWhatsappObj = whatsapps.find(w => w.id === Number(transferWhatsappId));
        if (selectedWhatsappObj && selectedWhatsappObj.status !== 'CONNECTED') {
          const whatsappName = selectedWhatsappObj.name || `Conexión ${selectedWhatsappObj.id}`;
          
          // ✅ MOSTRAR MODAL DE ADVERTENCIA en lugar de toast rápido
          setConnectionWarningData({
            connectionName: whatsappName,
            connectionStatus: selectedWhatsappObj.status
          });
          setConnectionWarningOpen(true);
          setIsLoading(false);
          return;
        }
      }
      
      // ✅ VALIDACIÓN MEJORADA: Solo requerir nombre y estatus para conexión básica
      let currentQueueId = selectedQueueId;
      let currentPrompt = selectedPrompt;
      let currentIntegration = selectedIntegration;
      
      // ✅ SIMPLIFICADO: Validación mutuamente exclusiva para DEPARTAMENTO | PROMPT | INTEGRACIÓN | FLOWBUILDER
      // ✅ Solo un departamento por conexión (cada departamento es independiente)
      const hasDepartment = currentQueueId && currentQueueId !== null && currentQueueId !== "" && currentQueueId !== 0;
      const hasPrompt = currentPrompt && currentPrompt !== null && currentPrompt !== "";
      const hasIntegration = currentIntegration && currentIntegration !== null && currentIntegration !== "";
      const hasFlowBuilder = selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "";
      
      // ✅ Contar cuántas opciones están activas
      const activeCount = [hasDepartment, hasPrompt, hasIntegration, hasFlowBuilder].filter(Boolean).length;
      
      // ✅ VALIDACIÓN: Solo UNA opción puede estar activa a la vez
      if (activeCount > 1) {
        toast.error("❌ Error: Solo puedes seleccionar UNA opción: Departamento, Prompt, Integración o FlowBuilder. No puedes seleccionar múltiples opciones simultáneamente.");
        setIsLoading(false);
        return;
      }
      
      // ✅ FORZAR LIMPIEZA SEGÚN LA OPCIÓN SELECCIONADA
      if (hasDepartment) {
        currentPrompt = '';
        currentIntegration = '';
        setSelectedFlowBuilder('');
      } else if (hasPrompt) {
        currentQueueId = '';
        currentIntegration = '';
        setSelectedFlowBuilder('');
      } else if (hasIntegration) {
        currentQueueId = '';
        currentPrompt = '';
        setSelectedFlowBuilder('');
      } else if (hasFlowBuilder) {
        currentQueueId = '';
        currentPrompt = '';
        currentIntegration = '';
        setSelectedPrompt('');
        setSelectedIntegration('');
      }
      
      // ✅ ELIMINAR VALIDACIÓN OBLIGATORIA DE DEPARTAMENTO/PROMPT
      // Una conexión puede existir solo con nombre y estatus
      // if (!hasDepartment && !hasPrompt) {
      //   toast.error("❌ Error: Debes seleccionar un departamento O un prompt para continuar.");
      //   setIsLoading(false);
      //   return;
      // }

      // ✅ ELIMINADO: Validación de chatbot - Ya no se usa (pestaña CHATBOT eliminada)
      // Los departamentos ahora trabajan de forma inteligente, no necesitan chatbot inicial

      // ✅ VALIDAR CAMPOS OBLIGATORIOS SEGÚN ESTADO DE CHECKBOXES
      // IMPORTANTE: Solo validar si las transferencias están ACTIVADAS
      if (tabStates.transfersEnabled) {
        // Verificar que ambos campos tengan contenido
        const hasTimeToTransfer = values.timeToTransfer && (typeof values.timeToTransfer === 'number' || (typeof values.timeToTransfer === 'string' && values.timeToTransfer.trim().length > 0));
        const hasTransferQueueId = transferQueueId && transferQueueId !== '';
        
        if (!hasTimeToTransfer || !hasTransferQueueId) {
          toast.error("❌ Error: Si activas Transferencias, debes configurar tiempo y departamento destino.");
          setIsLoading(false);
          return;
        }
      }
      // ✅ Si las transferencias están DESACTIVADAS, NO validar nada (los campos no se muestran)

      // ✅ VALIDAR CAMPOS OBLIGATORIOS SEGÚN ESTADO DE CHECKBOXES
      // IMPORTANTE: Solo validar si la inactividad está ACTIVADA
      if (tabStates.inactivityEnabled) {
        // Verificar que ambos campos tengan contenido
        const hasExpiresTicket = values.expiresTicket && (typeof values.expiresTicket === 'number' || (typeof values.expiresTicket === 'string' && values.expiresTicket.trim().length > 0));
        const hasExpiresMessage = values.expiresInactiveMessage && typeof values.expiresInactiveMessage === 'string' && values.expiresInactiveMessage.trim().length > 0;
        
        if (!hasExpiresTicket || !hasExpiresMessage) {
          toast.error("❌ Error: Si activas Cierre por Inactividad, debes configurar tiempo y mensaje.");
          setIsLoading(false);
          return;
        }
      }
      // ✅ Si la inactividad está DESACTIVADA, NO validar nada (los campos no se muestran)
      
      // ✅ PREPARAR DATOS CORRECTAMENTE
      // ✅ SIMPLIFICADO: Solo un departamento por conexión
      const whatsappData = {
        ...values, 
        queueIds: hasDepartment && currentQueueId 
          ? [currentQueueId] // ✅ Enviar como array de un solo elemento para compatibilidad con backend
          : [],
        // ✅ CORRECCIÓN: Enviar null si transfersEnabled está desactivado O si los campos están vacíos
        transferQueueId: tabStates.transfersEnabled && transferQueueId && transferQueueId !== '' ? transferQueueId : null,
        transferWhatsappId: tabStates.transfersEnabled && transferWhatsappId && transferWhatsappId !== '' ? transferWhatsappId : null,
        promptId: hasPrompt ? currentPrompt : null,
        integrationId: selectedIntegration && selectedIntegration !== null && selectedIntegration !== "" ? selectedIntegration : null,
        flowBuilderId: selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "" ? Number(selectedFlowBuilder) : null,
        type: whatsAppId ? (whatsApp?.type || connectionType) : connectionType,
        telegramApiId: values.telegramApiId || "",
        telegramApiHash: values.telegramApiHash || "",
        // ✅ CORRECCIÓN: Enviar null si transfersEnabled está desactivado, no hay transferQueueId, o timeToTransfer está vacío
        timeToTransfer: tabStates.transfersEnabled && transferQueueId && transferQueueId !== '' && values.timeToTransfer && values.timeToTransfer !== '' 
          ? (typeof values.timeToTransfer === 'string' ? (values.timeToTransfer.trim() !== '' ? values.timeToTransfer : null) : values.timeToTransfer)
          : null,
        // ✅ ELIMINADO: Campos de chatbot - Ya no se usan (pestaña CHATBOT eliminada)
        // Los departamentos ahora trabajan de forma inteligente, no necesitan chatbot inicial
        greetingMessage: '', // ✅ Siempre vacío (no se usa)
        complationMessage: '', // ✅ Siempre vacío (no se usa)
        outOfHoursMessage: '', // ✅ Siempre vacío (no se usa)
        ratingMessage: '', // ✅ Siempre vacío (no se usa)
        // ✅ CORRECCIÓN: Si la inactividad está DESACTIVADA, limpiar esos campos (0 para expiresTicket, '' para message)
        // ✅ Si está activada pero los campos están vacíos, también limpiarlos
        expiresTicket: tabStates.inactivityEnabled && values.expiresTicket && values.expiresTicket !== '' && values.expiresTicket !== 0 
          ? values.expiresTicket 
          : 0,
        expiresInactiveMessage: tabStates.inactivityEnabled && values.expiresInactiveMessage && values.expiresInactiveMessage.trim() !== '' 
          ? values.expiresInactiveMessage 
          : ''
      };
      
      
      delete whatsappData["queues"];
      delete whatsappData["session"];

      // ✅ FASE 2: Si estamos editando, verificar si hay cambios que afecten tickets
      if (whatsAppId) {
        try {
          const { data: preview } = await api.post(`/whatsapp/${whatsAppId}/preview-change`, {
            queueIds: whatsappData.queueIds,
            promptId: whatsappData.promptId,
            integrationId: whatsappData.integrationId,
            flowBuilderId: whatsappData.flowBuilderId
          });

          // Si hay cambios que requieren confirmación, mostrar modal
          if (preview.requiresConfirmation) {
            setPendingWhatsAppData(whatsappData);
            setPreviewData(preview);
            setPreviewModalOpen(true);
            setIsLoading(false);
            return; // Detener aquí, el guardado se hará después de confirmar
          }
        } catch (error) {
          console.error("❌ Error al obtener preview:", error);
          const errorMessage = error?.response?.data?.details || error?.response?.data?.error || "Error al validar cambios";
          toast.error(`⚠️ Error al validar cambios: ${errorMessage}. Se guardará sin validación.`);
          // Si falla el preview, continuar con el guardado normal pero informar al usuario
        }
      }

      // ✅ Guardar normalmente si no hay preview o no requiere confirmación
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      
      toast.success(i18n.t("whatsappModal.success"));
      
      if (onSave) {
        onSave();
      }
      
      handleClose();
    } catch (err) {
      console.error("❌ ERROR AL GUARDAR:", err);
      toastError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedQueueId, 
    selectedPrompt, 
    selectedIntegration,
    transferQueueId, // ✅ Agregar transferQueueId a las dependencias
    tabStates, 
    whatsAppId,
    transferWhatsappId,
    whatsapps, // ✅ Agregar whatsapps a las dependencias (usado en validación)
    onSave, 
    handleClose
  ]);

  // ✅ FASE 2: Función para confirmar y aplicar cambios después del preview
  const handleConfirmConfigChange = useCallback(async () => {
    try {
      if (!pendingWhatsAppData || !whatsAppId) {
        throw new Error("No hay datos pendientes para guardar");
      }

      // Aplicar cambios a tickets afectados
      await api.post(`/whatsapp/${whatsAppId}/apply-change`, {
        changeType: previewData.changeType,
        newConfig: {
          promptId: pendingWhatsAppData.promptId,
          integrationId: pendingWhatsAppData.integrationId,
          flowBuilderId: pendingWhatsAppData.flowBuilderId
        }
      });

      // Guardar configuración del WhatsApp
      await api.put(`/whatsapp/${whatsAppId}`, pendingWhatsAppData);
      
      toast.success(i18n.t("whatsappModal.success"));
      
      if (onSave) {
        onSave();
      }
      
      // Cerrar modal de preview
      setPreviewModalOpen(false);
      setPendingWhatsAppData(null);
      setPreviewData(null);
      
      // Cerrar modal principal
      handleClose();

      return { success: true, updatedTickets: previewData.affectedTickets, errors: 0 };
    } catch (error) {
      console.error("❌ Error al aplicar cambios:", error);
      throw error;
    }
  }, [pendingWhatsAppData, whatsAppId, previewData, onSave, handleClose]);

  // ✅ SIMPLIFICADO: Manejar cambio de departamento (solo uno)
  const handleChangeQueue = (selectedId) => {
    if (selectedId && selectedId !== null && selectedId !== "" && selectedId !== 0) {
      setSelectedPrompt('');
      setSelectedIntegration('');
      setSelectedQueueId(selectedId);
    } else {
      setSelectedPrompt('');
      setSelectedIntegration('');
      setSelectedQueueId('');
    }
  };

  // ✅ SIMPLIFICADO: Al seleccionar PROMPT, limpiar DEPARTAMENTO e INTEGRACIÓN
  const handleChangePrompt = (e) => {
    const value = e.target ? e.target.value : e;
    if (value && value !== null && value !== "") {
      setSelectedQueueId('');
      setSelectedIntegration('');
      setSelectedPrompt(value);
    } else if (!value || value === null || value === "") {
      setSelectedQueueId('');
      setSelectedIntegration('');
      setSelectedPrompt('');
    }
  };

  // ✅ SIMPLIFICADO: Al seleccionar INTEGRACIÓN, limpiar DEPARTAMENTO, PROMPT y FLOWBUILDER
  const handleChangeIntegration = (e) => {
    const raw = e.target ? e.target.value : e;
    const value = raw === "" || raw == null ? "" : Number(raw);
    if (value !== "" && !Number.isNaN(value)) {
      setSelectedQueueId('');
      setSelectedPrompt('');
      setSelectedFlowBuilder('');
      setSelectedIntegration(value);
    } else {
      setSelectedQueueId('');
      setSelectedPrompt('');
      setSelectedFlowBuilder('');
      setSelectedIntegration('');
    }
  };

  // ✅ ESTADO PARA ALMACENAR initialValues - Solo se actualiza cuando se cargan datos desde la API
  const [stableInitialValues, setStableInitialValues] = useState(null);

  // ✅ CALCULAR initialValues - Solo cambiar cuando se cargan datos desde la API
  // IMPORTANTE: Este useMemo solo recalcula cuando stableInitialValues cambia (datos desde API)
  // NO recalcula cuando el usuario escribe o cambia de pestaña, manteniendo los valores
  const formikInitialValues = useMemo(() => {
    // Si hay valores estables guardados, usar esos DIRECTAMENTE (misma referencia)
    // Esto evita que Formik se resetee cuando el usuario escribe o cambia de pestaña
    if (stableInitialValues) {
      return stableInitialValues;
    }
    
    // Si no hay valores estables, usar whatsApp (para crear nuevo WhatsApp)
    return {
      ...whatsApp,
      name: whatsApp.name || '',
      greetingMessage: whatsApp.greetingMessage || '',
      complationMessage: whatsApp.complationMessage || '',
      outOfHoursMessage: whatsApp.outOfHoursMessage || '',
      ratingMessage: whatsApp.ratingMessage || '',
      token: whatsApp.token !== null && whatsApp.token !== undefined ? whatsApp.token : '',
      status: whatsApp.status || 'OPENING',
      provider: whatsApp.provider || 'beta',
      expiresInactiveMessage: whatsApp.expiresInactiveMessage || '',
      expiresTicket: whatsApp.expiresTicket || 0,
      timeUseBotQueues: whatsApp.timeUseBotQueues || 0,
      maxUseBotQueues: whatsApp.maxUseBotQueues || 3,
      isDefault: whatsApp.isDefault || false,
      timeToTransfer: whatsApp.timeToTransfer || ''
    };
  }, [stableInitialValues, whatsApp]); // ✅ CORREGIDO: Añadir whatsApp como dependencia para el linter

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableEnforceFocus
        disableAutoFocus
        PaperProps={{
          style: {
            borderRadius: 16,
          },
        }}
      >
        <DialogTitle>
          {whatsAppId
            ? (connectionType === "telegram" || whatsApp?.type === "telegram" ? "Editar Telegram" : i18n.t("whatsappModal.title.edit"))
            : (connectionType === "telegram" ? "Agregar Telegram" : i18n.t("whatsappModal.title.add"))}
        </DialogTitle>
        
        <Formik
          key={whatsAppId || 'new'} // ✅ FORZAR RE-MOUNT solo cuando cambia el ID
          initialValues={formikInitialValues}
          enableReinitialize={true} // ✅ RE-INICIALIZAR cuando initialValues cambien (al cargar datos desde la API)
          validationSchema={SessionSchema}
          onSubmit={async (values, actions) => {
            // ✅ Validar campos requeridos manualmente antes de enviar
            // Esto evita el error "not focusable" al validar campos ocultos
            const errors = {};
            if (!values.name || values.name.trim().length === 0) {
              errors.name = 'Required';
            }
            
            if (Object.keys(errors).length > 0) {
              // Si hay errores, mostrar la pestaña que contiene el campo con error
              if (errors.name) {
                setActiveTab(0); // Ir a pestaña CONEXIÓN
              }
              actions.setErrors(errors);
              actions.setSubmitting(false);
              toast.error("Por favor completa los campos requeridos.");
              return;
            }
            
            
            setTimeout(() => {
              handleSaveWhatsApp(values); // ✅ PASAR VALORES DIRECTAMENTE
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => {
            // ✅ Calcular estados de automatización para deshabilitar campos
            const hasPromptInForm = selectedPrompt && selectedPrompt !== null && selectedPrompt !== "";
            const hasIntegrationInForm = selectedIntegration && selectedIntegration !== null && selectedIntegration !== "";
            const hasFlowBuilderInForm = selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "";
            const hasQueueInForm = selectedQueueId && selectedQueueId !== null && selectedQueueId !== "" && selectedQueueId !== 0;
            
            return (
              <Form>
              <DialogContent dividers className={classes.dialogContent}>
                {/* ✅ PESTAÑAS DE NAVEGACIÓN */}
                <Paper square style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                    aria-label="Configuración de WhatsApp"
                  >
                    <Tab label="Conexión" />
                    {/* ✅ ELIMINADO: Tab "Chatbot Inicial" - Ya no se usa */}
                    <Tab label="Transferencias" />
                  </Tabs>
                </Paper>

                {/* ✅ PESTAÑA 1: CONEXIÓN (OPTIMIZADA) */}
                <TabPanel value={activeTab} index={0} className={classes.tabPanel}>
                  {/* ✅ OPTIMIZADO: Header más compacto */}
                  <div className={classes.sectionHeader}>
                    <Typography variant="h6" color="primary" style={{ marginBottom: '4px' }}>
                      <span role="img" aria-label="conexión">🔌</span> Configuración
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                      {connectionType === "telegram" || whatsApp?.type === "telegram"
                        ? "Conexión de Telegram con GramJS. El QR se escanea desde la app de Telegram."
                        : "Configuración básica de la conexión de WhatsApp"}
                    </Typography>
                  </div>

                  <div className={classes.tabContent}>
                    {/* ✅ OPTIMIZADO: Campos en Grid de 2 columnas */}
                    <Grid container spacing={2}>
                      {/* Nombre + Token en la misma fila */}
                      <Grid item xs={12} sm={6}>
                        <Field name="name">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label={i18n.t("whatsappModal.form.name")}
                              autoFocus
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              required
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                              onChange={field.onChange}
                              InputProps={{
                                style: { fontSize: '0.875rem', height: '40px' }
                              }}
                              InputLabelProps={{
                                style: { fontSize: '0.875rem' }
                              }}
                              FormHelperTextProps={{
                                style: { fontSize: '0.75rem', marginTop: '4px' }
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field name="token">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Token (Opcional)"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.textFieldCompact}
                              value={field.value || ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          )}
                        </Field>
                      </Grid>

                      {(connectionType === "telegram" || whatsApp?.type === "telegram") && !whatsAppId ? (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Field name="telegramApiId">
                              {({ field }) => (
                                <TextField
                                  {...field}
                                  label="Telegram API ID"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  helperText="my.telegram.org → API development tools"
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field name="telegramApiHash">
                              {({ field }) => (
                                <TextField
                                  {...field}
                                  label="Telegram API Hash"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                />
                              )}
                            </Field>
                          </Grid>
                        </>
                      ) : null}
                      
                      {/* Status + Por defecto en la misma fila */}
                      <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControlCompact}>
                          <InputLabel style={{ fontSize: '0.875rem' }}>Status</InputLabel>
                          <Field
                            as={Select}
                            name="status"
                            label="Status"
                            required
                          >
                            <MenuItem value="OPENING" style={{ fontSize: '0.875rem', minHeight: '36px', padding: '6px 12px' }}>Abriendo</MenuItem>
                            <MenuItem value="CONNECTED" style={{ fontSize: '0.875rem', minHeight: '36px', padding: '6px 12px' }}>Conectado</MenuItem>
                            <MenuItem value="DISCONNECTED" style={{ fontSize: '0.875rem', minHeight: '36px', padding: '6px 12px' }}>Desconectado</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Field
                              as={Switch}
                              color="primary"
                              name="isDefault"
                              checked={values.isDefault}
                            />
                          }
                          label={i18n.t("whatsappModal.form.default")}
                        />
                      </Grid>
                      
                      {/* Departamento (full width) - Compacto */}
                      <Grid item xs={12}>
                        <div className={classes.compactSelectWrapper} style={{ opacity: (hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) ? 0.5 : 1, pointerEvents: (hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) ? 'none' : 'auto' }}>
                          <QueueSelect
                            selectedQueueIds={selectedQueueId}
                            onChange={(selectedId) => handleChangeQueue(selectedId)}
                            multiple={false}
                            title={<span style={{ fontSize: '0.875rem' }}><span role="img" aria-label="departamento">🏢</span> Departamento (Opcional)</span>}
                          />
                        </div>
                      </Grid>
                      
                      {/* Prompt + FlowBuilder en la misma fila - Compactos */}
                      <Grid item xs={12} sm={6}>
                        <div className={classes.compactSelectWrapper}>
                          <PromptSelect
                            selectedPromptId={selectedPrompt}
                            onChange={handleChangePrompt}
                            disabled={hasIntegrationInForm || hasFlowBuilderInForm}
                            title={<span style={{ fontSize: '0.875rem' }}><span role="img" aria-label="prompt">🤖</span> {i18n.t("whatsappModal.form.prompt")} (Opcional)</span>}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          className={classes.formControlCompact}
                        >
                          <InputLabel id="flowBuilderId-selection-label" style={{ fontSize: '0.875rem' }}>
                            Flowbuilder (Opcional)
                          </InputLabel>
                          <Select
                            label="Flowbuilder (Opcional)"
                            name="flowBuilderId"
                            id="flowBuilderId"
                            labelId="flowBuilderId-selection-label"
                            value={selectedFlowBuilder ? String(selectedFlowBuilder) : ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedFlowBuilder(value);
                              // ✅ Si se selecciona FlowBuilder, limpiar otros campos
                              if (value && value !== "") {
                                setSelectedPrompt('');
                                setSelectedIntegration('');
                                setSelectedQueueId('');
                              }
                            }}
                            disabled={loadingFlowBuilders || hasPromptInForm || hasIntegrationInForm || hasQueueInForm}
                            style={{ fontSize: '0.875rem', height: '40px' }}
                            MenuProps={{
                              PaperProps: {
                                style: { maxHeight: 300 }
                              }
                            }}
                          >
                            <MenuItem value="" style={{ fontSize: '0.875rem', minHeight: '36px', padding: '6px 12px' }}>Sin flujo</MenuItem>
                            {flowBuilders.map((flow) => (
                              <MenuItem key={flow.id} value={String(flow.id)} style={{ fontSize: '0.875rem', minHeight: '36px', padding: '6px 12px' }}>
                                {flow.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      {/* Integración (full width) - Compacto */}
                      <Grid item xs={12}>
                        <div className={classes.compactSelectWrapper}>
                          <IntegrationSelect
                            selectedIntegrationId={selectedIntegration}
                            onChange={handleChangeIntegration}
                            title={<span style={{ fontSize: '0.875rem' }}><span role="img" aria-label="integración">🔗</span> Integración (Opcional)</span>}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                  
                  {/* ✅ BOTONES DENTRO DE LA PESTAÑA CONEXIÓN - MEJOR VISIBILIDAD */}
                  {activeTab === 0 && (
                    <Box 
                      style={{ 
                        padding: theme.spacing(2, 3),
                        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
                        backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(2),
                        marginTop: theme.spacing(2),
                      }}
                    >
                      <Button
                        onClick={handleClose}
                        color="secondary"
                        disabled={isSubmitting || isLoading}
                        variant="outlined"
                        style={{
                          minWidth: '120px',
                          fontWeight: 600,
                        }}
                      >
                        {i18n.t("whatsappModal.buttons.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitting || isLoading}
                        variant="contained"
                        style={{
                          minWidth: '120px',
                          fontWeight: 600,
                          boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                        }}
                      >
                        {whatsAppId
                          ? i18n.t("whatsappModal.buttons.okEdit")
                          : i18n.t("whatsappModal.buttons.okAdd")}
                        {((isSubmitting || isLoading)) && (
                          <CircularProgress
                            size={20}
                            style={{ marginLeft: theme.spacing(1) }}
                          />
                        )}
                      </Button>
                    </Box>
                  )}
                </TabPanel>

                {/* ✅ ELIMINADO: PESTAÑA 2: CHATBOT INICIAL - Ya no se usa */}
                {/* Los departamentos ahora trabajan de forma inteligente, no necesitan chatbot inicial */}

                {/* ✅ PESTAÑA 2: TRANSFERENCIAS (OPTIMIZADA) */}
                <TabPanel value={activeTab} index={1} className={classes.tabPanel}>
                  {/* ✅ OPTIMIZADO: Header más compacto */}
                  <div className={classes.sectionHeader}>
                    <Typography variant="h6" color="primary" style={{ marginBottom: '4px' }}>
                      <span role="img" aria-label="transferencia">🔄</span> Transferencias Automáticas
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                      Configuración de transferencias por tiempo e inactividad
                    </Typography>
                  </div>

                  {/* ✅ AGRUPACIÓN 1: TRANSFERENCIAS POR TIEMPO */}
                  <div className={classes.checkboxSection}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={tabStates.transfersEnabled}
                          onChange={(e) => handleCheckboxChange('transfersEnabled', e.target.checked, setFieldValue)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="subtitle1" color="primary" style={{ fontWeight: 600 }}>
                          <span role="img" aria-label="transferencia">🔄</span> Activar Transferencias por Tiempo
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 24, fontSize: '0.85rem' }}>
                      {tabStates.transfersEnabled 
                        ? "Transferir tickets automáticamente después de un tiempo específico sin respuesta"
                        : "Los campos están deshabilitados y se mantendrán vacíos"
                      }
                    </Typography>
                  </div>

                  {tabStates.transfersEnabled && (
                    <div className={classes.tabContent}>
                      <Grid container spacing={2}>
                        {/* ✅ OPTIMIZADO: Minutos + Departamento en la misma fila - COMPACTOS */}
                        <Grid item xs={12} sm={6}>
                          <Field name="timeToTransfer">
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                fullWidth
                                type="number"
                                label={<span style={{ fontSize: '0.875rem' }}><span role="img" aria-label="cronómetro">⏱️</span> Minutos</span>}
                                error={meta.touched && Boolean(meta.error)}
                                helperText={(meta.touched && meta.error) || "Ej: 30 = transferir después de 30 minutos"}
                                variant="outlined"
                                margin="dense"
                                inputProps={{ min: 1, step: 1, style: { fontSize: '0.875rem', padding: '8px 12px', height: '40px' } }}
                                className={classes.textFieldCompact}
                                value={field.value || ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                InputLabelProps={{
                                  style: { fontSize: '0.875rem' }
                                }}
                                FormHelperTextProps={{
                                  style: { fontSize: '0.75rem', marginTop: '4px' }
                                }}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <div className={classes.compactSelectWrapper}>
                            <QueueSelect
                              selectedQueueIds={transferQueueId}
                              onChange={(selectedId) => {
                                setTransferQueueId(selectedId)
                                if (!selectedId) {
                                  setFieldValue('timeToTransfer', '');
                                }
                              }}
                              multiple={false}
                              title={<span style={{ fontSize: '0.875rem' }}><span role="img" aria-label="diana">🎯</span> Departamento Destino</span>}
                              required
                            />
                          </div>
                        </Grid>
                        {/* ✅ Conexión WhatsApp (full width) - COMPACTO */}
                        <Grid item xs={12}>
                          <FormControl 
                            fullWidth 
                            variant="outlined" 
                            margin="dense"
                            className={classes.formControlCompact}
                          >
                            <InputLabel style={{ fontSize: '0.875rem' }}>
                              <span role="img" aria-label="Conexión WhatsApp">📱</span> Conexión WhatsApp (Opcional)
                            </InputLabel>
                            <Select
                              value={transferWhatsappId}
                              onChange={(e) => {
                                setTransferWhatsappId(e.target.value);
                              }}
                              label={
                                <span>
                                  <span role="img" aria-label="Conexión WhatsApp">📱</span> Conexión WhatsApp (Opcional)
                                </span>
                              }
                              disabled={loadingWhatsapps}
                              style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
                              renderValue={() => {
                                if (transferWhatsappId === "" || !transferWhatsappId) {
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                                      <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                                      <span>Usar conexión del ticket (por defecto)</span>
                                    </div>
                                  );
                                }
                                const whatsapp = whatsapps.find(w => w.id === Number(transferWhatsappId));
                                if (!whatsapp) {
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                                      <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                                      <span>Seleccione una conexión</span>
                                    </div>
                                  );
                                }
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                      <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                        {whatsapp.name || `Conexión ${whatsapp.id}`}
                                      </span>
                                      <span style={{ fontSize: '12px', color: '#666' }}>
                                        {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                                         whatsapp.name ? whatsapp.name : 
                                         `Conexión ${whatsapp.id}`}
                                      </span>
                                    </div>
                                  </div>
                                );
                              }}
                            >
                              <MenuItem value="">
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="Por defecto">🔄</span>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                      Usar conexión del ticket (por defecto)
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                      El ticket se transferirá usando la misma conexión WhatsApp
                                    </span>
                                  </div>
                                </div>
                              </MenuItem>
                              {whatsapps && Array.isArray(whatsapps) && whatsapps.map((whatsapp) => (
                                <MenuItem key={whatsapp.id} value={whatsapp.id} style={{ fontSize: '0.875rem', minHeight: '36px', padding: '6px 12px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <span style={{ marginRight: '6px', fontSize: '14px' }} role="img" aria-label="WhatsApp">📱</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                                      <span style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                                        {whatsapp.name || `Conexión ${whatsapp.id}`}
                                      </span>
                                      <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                        {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                                         whatsapp.name ? whatsapp.name : 
                                         `Conexión ${whatsapp.id}`}
                                      </span>
                                    </div>
                                    <span style={{ 
                                      padding: '2px 6px', 
                                      borderRadius: '10px', 
                                      fontSize: '0.65rem',
                                      backgroundColor: whatsapp.status === 'CONNECTED' ? '#4CAF50' : '#f44336',
                                      color: 'white',
                                      marginLeft: '6px'
                                    }}>
                                      {whatsapp.status === 'CONNECTED' ? 'CONECTADO' : 'DESCONECTADO'}
                                    </span>
                                  </div>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </div>
                  )}

                  {/* ✅ AGRUPACIÓN 2: CIERRE POR INACTIVIDAD */}
                  <div className={classes.checkboxSection} style={{ marginTop: 16 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={tabStates.inactivityEnabled}
                          onChange={(e) => handleCheckboxChange('inactivityEnabled', e.target.checked, setFieldValue)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="subtitle1" color="primary" style={{ fontWeight: 600 }}>
                          <span role="img" aria-label="reloj">⏰</span> Activar Cierre por Inactividad
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 24, fontSize: '0.85rem' }}>
                      {tabStates.inactivityEnabled 
                        ? "Cerrar tickets automáticamente después de un período de inactividad"
                        : "Los campos están deshabilitados y se mantendrán vacíos"
                      }
                    </Typography>
                  </div>

                  {tabStates.inactivityEnabled && (
                    <div className={classes.tabContent}>
                      <Grid container spacing={2}>
                        {/* ✅ OPTIMIZADO: Minutos + Mensaje en la misma fila - COMPACTOS */}
                        <Grid item xs={12} sm={6}>
                          <Field name="expiresTicket">
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                fullWidth
                                type="number"
                                label={<span style={{ fontSize: '0.875rem' }}><span role="img" aria-label="reloj">⏰</span> Minutos</span>}
                                variant="outlined"
                                margin="dense"
                                error={meta.touched && Boolean(meta.error)}
                                helperText={(meta.touched && meta.error) || "Ej: 60 = cerrar después de 60 minutos"}
                                inputProps={{ min: 1, step: 1, style: { fontSize: '0.875rem', padding: '8px 12px', height: '40px' } }}
                                className={classes.textFieldCompact}
                                value={field.value || ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                InputLabelProps={{
                                  style: { fontSize: '0.875rem' }
                                }}
                                FormHelperTextProps={{
                                  style: { fontSize: '0.75rem', marginTop: '4px' }
                                }}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field name="expiresInactiveMessage">
                            {({ field, meta, form }) => (
                              <CampaignMessageComposer
                                label="Mensaje de Cierre"
                                value={field.value || ""}
                                onChange={(next) => form.setFieldValue(field.name, next)}
                                error={meta.touched && Boolean(meta.error)}
                                helperText={(meta.touched && meta.error) || "Mensaje enviado antes de cerrar"}
                                placeholder="Mensaje enviado antes de cerrar"
                                rows={3}
                                maxLength={1000}
                                showCounter
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </TabPanel>
              </DialogContent>

              {/* ✅ BOTONES PARA PESTAÑA TRANSFERENCIAS - Solo mostrar si NO está en pestaña CONEXIÓN */}
              {activeTab !== 0 && (
                <DialogActions className={classes.dialogActions}>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting || isLoading}
                    variant="outlined"
                    style={{
                      minWidth: '120px',
                      fontWeight: 600,
                    }}
                  >
                    {i18n.t("whatsappModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || isLoading}
                    variant="contained"
                    style={{
                      minWidth: '120px',
                      fontWeight: 600,
                      boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                    }}
                  >
                    {whatsAppId
                      ? i18n.t("whatsappModal.buttons.okEdit")
                      : i18n.t("whatsappModal.buttons.okAdd")}
                    {((isSubmitting || isLoading)) && (
                      <CircularProgress
                        size={20}
                        style={{ marginLeft: theme.spacing(1) }}
                      />
                    )}
                  </Button>
                </DialogActions>
              )}
              </Form>
            );
          }}
          </Formik>
      </Dialog>

      {/* ✅ NUEVO: Modal de advertencia de conexión desconectada */}
      <ConnectionWarningModal
        open={connectionWarningOpen}
        onClose={() => {
          setConnectionWarningOpen(false);
          setConnectionWarningData(null);
        }}
        connectionName={connectionWarningData?.connectionName || ""}
        connectionStatus={connectionWarningData?.connectionStatus || ""}
      />

      {/* ✅ FASE 2: Modal de preview de cambios de configuración */}
      <ConfigChangePreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPendingWhatsAppData(null);
          setPreviewData(null);
        }}
        previewData={previewData}
        onConfirm={handleConfirmConfigChange}
        loading={isLoading}
        type="whatsapp"
      />
    </div>
  );
};

export default React.memo(WhatsAppModal);
